import React, { MouseEvent } from 'react';
import { Link } from 'react-router-dom';

import Staffer from '../../Staffer/Staffer';
import Bubble from '../../Bubble/Bubble';

import { bemHelper, injectChild } from '../../../helpers';
import getGlobalProps from '../../../helpers/constants/global-props';

export interface NavItemProps {
  /** Unique Identifier */
  id: string;
  /** Label of item */
  label: React.ReactNode;
  /** Href */
  href?: string;
  /** Vertical version of nav item */
  isVertical?: boolean;
  /** Active item */
  isActive?: boolean;
  /** onClick callback */
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  /** Is using React Router */
  withReactRouter?: boolean;
  /** For dropdowns inside the Nav. The label must be a Dropdown component */
  isDropdown?: boolean;
  /** For buttons inside the Nav */
  isButton?: boolean;
  /** Staff only items */
  isStaff?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Makes item content wrap for long texts (only works with vertical navs) */
  wrapText?: boolean;
}

const globalProps = getGlobalProps({ sunset: true, inkSass: true });

/**
 * @purpose Splits navbar into items
 * @deprecated This component is deprecated and replaced by [HorizontalNav](https://ink.carta.com/components/HorizontalNav/), [VerticalNav](https://ink.carta.com/components/VerticalNav/) or [SubNav](https://ink.carta.com/components/SubNav/)
 * @status deprecated
 * @replacementComponent HorizontalNav, VerticalNav, SubNav
 * @dateOfDeletion September 1st, 2023
 */
function NavItem(props: NavItemProps) {
  function defineClassName() {
    const b = bemHelper();

    return b('ink-nav--item', {
      vertical: props.isVertical,
      active: props.isActive,
      wrap: props.wrapText && props.isVertical,
    });
  }

  function renderLabel() {
    if (props.isStaff) {
      return (
        <>
          <span>{props.label}</span>
          <Bubble variant="feedback-staff">Staff</Bubble>
        </>
      );
    }
    return props.label;
  }

  function renderDropdownContent() {
    return injectChild(props.label, { isNavItem: true, isStaff: props.isStaff, isActive: props.isActive });
  }

  function renderContent() {
    if (props.isButton) {
      return renderLabel();
    }

    const className = defineClassName();

    if (props.withReactRouter) {
      return (
        <Link to={props.href} className={className} onClick={props.onClick}>
          {renderLabel()}
        </Link>
      );
    }

    return (
      <a href={props.href} className={className} onClick={props.onClick}>
        {renderLabel()}
      </a>
    );
  }

  function renderNavItem() {
    return (
      <li {...globalProps} id={props.id} data-testid={props['data-testid']}>
        {props.isDropdown ? renderDropdownContent() : renderContent()}
      </li>
    );
  }

  return props.isStaff ? <Staffer isInvisible>{renderNavItem()}</Staffer> : renderNavItem();
}

NavItem.defaultProps = {
  isVertical: false,
  isActive: false,
  onClick: undefined,
  href: undefined,
  withReactRouter: false,
  isDropdown: false,
  isButton: false,
  isStaff: false,
  'data-testid': undefined,
  wrapText: false,
};

export default NavItem;
